import type {
  HomeShowcasesNormalized,
  HomeShowcasesPayload,
  ShowcaseArticleCardData,
  ShowcaseProfessionCardData,
  ShowcaseSchoolCardData,
} from '~/types/homeShowcases'

export type { HomeShowcasesNormalized, HomeShowcasesPayload } from '~/types/homeShowcases'

const SHOWCASE_MAX_ITEMS = 2

/** État vide avant chargement du fallback. */
export const EMPTY_HOME_SHOWCASES: HomeShowcasesNormalized = {
  schools: [],
  professions: [],
  articles: [],
}

export const DEFAULT_HOME_SHOWCASES_FALLBACK_FILE = 'home-showcases-fallback.json'

/** Fond hero si l'image ne charge pas (toutes les cartes showcase). */
export const SHOWCASE_HERO_FALLBACK_BACKGROUND = 'linear-gradient(135deg, #2a2a3e, #1a1a2e)'

export function cloneHomeShowcases(data: HomeShowcasesNormalized): HomeShowcasesNormalized {
  return structuredClone(data)
}

function isValidSchool(item: ShowcaseSchoolCardData): boolean {
  return Boolean(item.name?.trim() && item.subtitle?.trim() && item.backgroundImage?.trim())
}

function isValidProfession(item: ShowcaseProfessionCardData): boolean {
  return Boolean(item.name?.trim() && item.backgroundImage?.trim())
}

function isValidArticle(item: ShowcaseArticleCardData): boolean {
  return Boolean(item.title?.trim() && item.image?.trim())
}

/**
 * Fusionne un `HomeShowcasesPayload` : chaque tableau API/JSON non vide remplace la clé.
 * @param {HomeShowcasesNormalized} base - Données de départ.
 * @param {HomeShowcasesPayload | null | undefined} raw - JSON et/ou fragment API.
 * @returns {HomeShowcasesNormalized} Données fusionnées.
 */
export function mergeHomeShowcases(
  base: HomeShowcasesNormalized,
  raw: HomeShowcasesPayload | null | undefined,
): HomeShowcasesNormalized {
  if (!raw) return base

  const merged: HomeShowcasesNormalized = cloneHomeShowcases(base)

  if (raw.schools?.length) {
    const schools: ShowcaseSchoolCardData[] = raw.schools
      .filter(isValidSchool)
      .slice(0, SHOWCASE_MAX_ITEMS)
    if (schools.length > 0) merged.schools = schools
  }

  if (raw.professions?.length) {
    const professions: ShowcaseProfessionCardData[] = raw.professions
      .filter(isValidProfession)
      .slice(0, SHOWCASE_MAX_ITEMS)
    if (professions.length > 0) merged.professions = professions
  }

  if (raw.articles?.length) {
    const articles: ShowcaseArticleCardData[] = raw.articles
      .filter(isValidArticle)
      .slice(0, SHOWCASE_MAX_ITEMS)
    if (articles.length > 0) merged.articles = articles
  }

  return merged
}
